/**
 * Mobile toggle navigation with improved accessibility
 */
import { getFocusableElements } from './a11y.js';
import { safeSelect, announceForScreenReader } from './utils.js';

/**
 * Initialisiert die Mobile-Navigation
 */
export const initMobileNavigation = (function() {
	let navToggle, overlay, closeButton, body;
	let labels = {};
	
	/**
	 * Navigation öffnen
	 */
	function openNavigation() {
		try {
			if (!navToggle || !overlay) return;
			
			// ARIA-Status setzen
			navToggle.setAttribute('aria-expanded', 'true');
			overlay.setAttribute('aria-hidden', 'false');
			
			// CSS-Klassen aktualisieren (BEM-konform mit is- Präfix)
			overlay.classList.add('site-overlay--is-open');
			navToggle.classList.add('btn-overlay-open--is-active');
			
			if (body) {
				body.classList.add('body--nav-open');
			}
			
			// Screen reader text aktualisieren
			const srText = safeSelect('.screen-reader-text', navToggle);
			if (srText) {
				srText.textContent = labels.closeMenu;
			}
			
			// Statusänderung ankündigen
			announceForScreenReader(labels.menuOpened);
			
			// Tab-Fokus eingrenzen und erstes fokussierbares Element fokussieren
			const focusableElements = getFocusableElements(overlay);
			if (focusableElements.length > 0) {
				// Timeout für Animation
				setTimeout(() => {
					focusableElements[0].focus();
				}, 100);
			}
			
			// Tabindex für alle fokussierbaren Elemente setzen
			focusableElements.forEach(el => {
				if (el) {
					el.setAttribute('tabindex', '0');
				}
			});
		} catch (error) {
			console.error('Fehler beim Öffnen der Navigation:', error);
		}
	}

	/**
	 * Navigation schließen
	 */
	function closeNavigation() {
		try {
			if (!navToggle || !overlay) return;
			
			// ARIA-Status setzen
			navToggle.setAttribute('aria-expanded', 'false');
			overlay.setAttribute('aria-hidden', 'true');
			
			// CSS-Klassen aktualisieren
			overlay.classList.remove('site-overlay--is-open');
			navToggle.classList.remove('btn-overlay-open--is-active');
			
			if (body) {
				body.classList.remove('body--nav-open');
			}
			
			// Screen reader text aktualisieren
			const srText = safeSelect('.screen-reader-text', navToggle);
			if (srText) {
				srText.textContent = labels.openMenu;
			}
			
			// Statusänderung ankündigen
			announceForScreenReader(labels.menuClosed);
			
			// Tabindex zurücksetzen
			const focusableElements = getFocusableElements(overlay);
			focusableElements.forEach(el => {
				if (el) {
					el.setAttribute('tabindex', '-1');
				}
			});
		} catch (error) {
			console.error('Fehler beim Schließen der Navigation:', error);
		}
	}

	/**
	 * Navigation umschalten
	 */
	function toggleNavigation() {
		try {
			if (!navToggle) return;
			
			const isExpanded = navToggle.getAttribute('aria-expanded') === 'true';
			const newState = !isExpanded;
			
			if (newState) {
				openNavigation();
			} else {
				closeNavigation();
			}
		} catch (error) {
			console.error('Fehler beim Umschalten der Navigation:', error);
		}
	}
	
	// Öffentliche Schnittstelle
	return function() {
		try {
			// DOM-Elemente selektieren
			navToggle = safeSelect('.button-overlay-open');
			overlay = document.getElementById('overlay');
			closeButton = safeSelect('.button-overlay-close');
			body = document.body;
			
			// Prüfen, ob erforderliche Elemente existieren
			if (!navToggle || !overlay) {
				console.warn('Erforderliche Navigation-Elemente nicht gefunden');
				return;
			}
			
			// ID des kontrollierten Elements
			const overlayId = overlay?.id || 'overlay';
			
			labels = {
				openMenu:   navToggle.dataset.labelOpen,
				closeMenu:  navToggle.dataset.labelClose,
				menuOpened: navToggle.dataset.labelOpened,
				menuClosed: navToggle.dataset.labelClosed,
			};
			
			// Initialisierung der Zustände
			navToggle.setAttribute('aria-expanded', 'false');
			navToggle.setAttribute('aria-controls', overlayId);
			navToggle.setAttribute('aria-haspopup', 'dialog');
			
			// Overlay-Eigenschaften für Barrierefreiheit
			overlay.setAttribute('aria-hidden', 'true');
			overlay.setAttribute('aria-modal', 'true');
			overlay.setAttribute('role', 'dialog');
			overlay.setAttribute('aria-labelledby', 'overlay-title');
			
			// Event-Listener für Klick auf Toggle-Button
			navToggle.addEventListener('click', (e) => {
				e.preventDefault();
				toggleNavigation();
			});
			
			// Tastaturnavigation für Toggle-Button
			navToggle.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					toggleNavigation();
				}
			});
			
			// Klick außerhalb schließt Menü durch Event-Delegation
			document.addEventListener('click', (e) => {
				if (
					navToggle && 
					overlay && 
					!navToggle.contains(e.target) &&
					!overlay.contains(e.target) &&
					overlay.classList.contains('site-overlay--is-open')
				) {
					closeNavigation();
				}
			});
			
			// ESC-Taste schließt Menü 
			document.addEventListener('keydown', (e) => {
				if (e.key === 'Escape' && overlay && overlay.classList.contains('site-overlay--is-open')) {
					closeNavigation();
					if (navToggle) {
						navToggle.focus();
					}
				}
			});
			
			// Close-Button-Handler
			if (closeButton) {
				closeButton.addEventListener('click', (e) => {
					e.preventDefault();
					closeNavigation();
				});
			}
		} catch (error) {
			console.error('Fehler bei der Initialisierung der mobilen Navigation:', error);
		}
	};
})();